import { useCallback } from 'react'

import { configPublic } from '@src/config.public'
import { type UseClickActionArgs } from '@src/hooks/useClickAction/useClickActionArgs'
import { useSKUSelectionStore } from '@src/stores/SKUSelectionStore'
import { useTrackAddToCart, useTrackSelectCta } from '@src/utils/analytics.util'

export const useTrackAction = ({
  actionName,
  analyticsModule,
}: {
  actionName: UseClickActionArgs['actionName']
  analyticsModule?: string
}) => {
  const trackSelectCta = useTrackSelectCta()
  const trackAddToCart = useTrackAddToCart()
  const getSelectedSKUs = useSKUSelectionStore((state) => state.getSelectedSKUs)

  const trackAction = useCallback(
    async (props: UseClickActionArgs, cart: Commercetools.Cart | null) => {
      if (actionName === 'addItemsToCart') {
        if (configPublic.featureFlags.shopifyEnabled) {
          // TODO: implement tracking for shopify add to cart
        } else {
          await trackAddToCart(
            cart as Commercetools.Cart,
            props?.addItemsToCart?.skus ?? [],
            props?.addItemsToCart?.redirectUrl,
            analyticsModule
          )
        }
        return
      }

      if (actionName === 'upsellToCart') {
        await trackAddToCart(
          cart as Commercetools.Cart,
          props?.upsellToCart?.skus ?? [],
          props?.upsellToCart?.redirectUrl,
          analyticsModule
        )
        return
      }

      if (actionName === 'revertUpsell') {
        trackSelectCta({ 'cta url': props?.revertUpsell?.redirectUrl, module: analyticsModule })
      }

      if (actionName === 'addSelectedSKUsToCart') {
        const selectedSkus = getSelectedSKUs()
        const extraSkus = props?.addSelectedSKUsToCart?.extraSkus?.map((val) => val.sku) ?? []
        const allSkus = [...selectedSkus, ...extraSkus].map((sku) => ({
          sku,
          quantity: 1,
        }))
        await trackAddToCart(
          cart as Commercetools.Cart,
          allSkus,
          props?.addSelectedSKUsToCart?.redirectUrl,
          analyticsModule
        )
        return
      }
    },
    [actionName, analyticsModule, getSelectedSKUs, trackAddToCart, trackSelectCta]
  )

  return { trackAction }
}
